<template>
  <div
    v-if="props.prefix || props.suffix"
    class="currency-input"
    role="presentation"
    @click="focusInput"
  >
    <span v-if="props.prefix" class="currency-input__prefix" aria-hidden="true">
      {{ props.prefix }}
    </span>
    <input
      v-bind="$attrs"
      :id="props.id"
      ref="inputRef"
      class="formatted-input__field currency-input__field"
      type="text"
      :inputmode="props.wholeNumber ? 'numeric' : 'decimal'"
      :max="props.max"
      :pattern="props.wholeNumber ? '[0-9]*' : '[0-9,.]*'"
      placeholder="0"
      :value="displayValue"
      :style="{ width: `${Math.max(displayValue.length, 1)}ch` }"
      @input="handleInput"
      @blur="formatDisplayValue"
      @change="handleCommit"
    />
    <span v-if="props.suffix" class="currency-input__suffix" aria-hidden="true">
      {{ props.suffix }}
    </span>
  </div>
  <input
    v-else
    v-bind="$attrs"
    :id="props.id"
    class="formatted-input__field"
    type="text"
    :inputmode="props.wholeNumber ? 'numeric' : 'decimal'"
    :max="props.max"
    :pattern="props.wholeNumber ? '[0-9]*' : '[0-9,.]*'"
    placeholder="0"
    :value="displayValue"
    :style="{ width: `${Math.max(displayValue.length, 1)}ch` }"
    @input="handleInput"
    @blur="formatDisplayValue"
    @change="handleCommit"
  />
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  id: string;
  modelValue: number | null;
  wholeNumber?: boolean;
  max?: number;
  prefix?: string;
  suffix?: string;
}>();

const emit = defineEmits<{
  (event: "update:modelValue" | "commit", value: number): void;
}>();

const decimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
const wholeNumberFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const inputRef = ref<HTMLInputElement | null>(null);

const focusInput = (): void => {
  inputRef.value?.focus();
};

const sanitizeCurrencyText = (raw: string): string => {
  if (props.wholeNumber) {
    const [integerPart = ""] = raw.replace(/[^\d.,]/g, "").split(".", 1);
    const digits = integerPart.replace(/,/g, "");
    if (digits === "") return "";

    const value = Number(digits);
    return String(props.max === undefined ? value : Math.min(value, props.max));
  }

  const filtered = raw.replace(/[^\d.,]/g, "");
  const [integer = "", ...fractionParts] = filtered.split(".");
  if (fractionParts.length === 0) return integer;

  const fraction = fractionParts.join("").replace(/,/g, "").slice(0, 2);
  return `${integer}.${fraction}`;
};

const parseCurrencyText = (raw: string): number => {
  const normalized = raw.replace(/,/g, "");
  if (normalized === "" || normalized === ".") return 0;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return 0;

  const value = props.wholeNumber ? Math.trunc(parsed) : parsed;
  return props.max === undefined ? value : Math.min(value, props.max);
};

const formatModelValue = (value: number | null): string => {
  if (value === null || value === 0 || !Number.isFinite(value)) return "";

  const normalizedValue = props.max === undefined ? value : Math.min(value, props.max);
  return (props.wholeNumber ? wholeNumberFormatter : decimalFormatter).format(normalizedValue);
};

const displayValue = ref(formatModelValue(props.modelValue));

watch(
  () => props.modelValue,
  (value) => {
    const normalizedValue = value ?? 0;
    if (parseCurrencyText(displayValue.value) !== normalizedValue) {
      displayValue.value = formatModelValue(value);
    }
  }
);

const handleInput = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const sanitized = sanitizeCurrencyText(input.value);
  input.value = sanitized;
  displayValue.value = sanitized;
  emit("update:modelValue", parseCurrencyText(sanitized));
};

const handleCommit = (): void => {
  emit("commit", parseCurrencyText(displayValue.value));
};

const formatDisplayValue = (): void => {
  if (displayValue.value === "") return;
  const formatter = props.wholeNumber ? wholeNumberFormatter : decimalFormatter;
  displayValue.value = formatter.format(parseCurrencyText(displayValue.value));
};
</script>

<style scoped>
.currency-input {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 2.75rem;
  padding: 0 0.75rem;
  border: 1px solid var(--rds-dark-1, #747474);
  background: #fff;
  color: var(--rds-dark-1, #747474);
}

.currency-input__field {
  width: auto;
  min-width: 1ch;
  max-width: 100%;
  flex: 0 1 auto;
  padding: 0;
  border: 0;
  box-shadow: none;
  background: transparent;
}

.formatted-input__field::placeholder {
  color: var(--rds-dark-1, #747474);
  opacity: 1;
}

.currency-input__field:focus {
  border: 0;
  box-shadow: none;
  outline: 0;
}

.currency-input:focus-within {
  border-color: var(--rds-dark-1, #747474);
  box-shadow: 0 0 0 0.2rem rgb(0 0 0 / 12%);
}

.currency-input__prefix,
.currency-input__suffix {
  flex: 0 0 auto;
  white-space: nowrap;
}

.currency-input__suffix {
  margin-left: 0.35rem;
}

/* .formatted-input {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  border-bottom: 1px solid #ccc;
  padding: 4px 0;
}

.formatted-input__field {
  border: 0;
  outline: none;
  font: inherit;
  width: auto;
  min-width: 2ch;
  background: transparent;
  padding: 0;
}

.formatted-input__suffix {
  white-space: nowrap;
} */
</style>
